<template>
    <div class="modal-selection-container">
        <header v-if="loading || addObjectsLoading">
            <div class="spinner-container spinner-container-modal">
                <svg class="spinner" width="65px" height="65px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
                </svg><br />
                <transition name="text-fade" mode="in-out">
                    <span v-if="loading" style="position: absolute; top: 95px;">Henter objekter..</span>
                    <span v-else style="position: absolute; top: 95px;">Tilføjer objekter til aktivitet..</span>
                </transition>
            </div>
        </header>
        <header v-else>
            <transition name="text-fade" mode="out-in">
                <h1>{{`${filteredObjects ? filteredObjects.length : objects.length} af ${objects.length} objekter valgt`}}</h1>
            </transition>
        </header>

        <template v-if="!addObjectsLoading">
            <h3 style="color: red;" :key="limitWarning" v-if="limitWarning"><br />Det valgte område indeholder mere end {{warningAmount}} objekter, vælg venligst et mindre område.</h3>

            <div class="main">
                <div class="filter-text">
                    <icon class="layers smaller-svg" name="layers" />
                    <b>&nbsp;&nbsp;LAG</b>
                </div>

                <select class="layer-selection select-options" :disabled="loading" v-model="selectedLayer">
                    <option value="" disabled>Vælg lag</option>
                    <option v-for="(layer,index) in getLayers" :value="layer" :key="index">
                        {{ layer.name }}
                    </option>
                </select>
                <br />
                <div class="filter-text" v-if="filters.length > 0">
                    <icon class="layers smaller-svg" name="filter" />
                    <b>&nbsp;&nbsp;FILTER</b> (<small><em>Match alle filtre</em></small>)
                </div>

                <div class="filter-container">
                    <div class="filter-list" v-if="filters.length > 0">
                        <template v-for="(filter, index) in filters">
                            <div class="flexbox-center" v-if="index > 0">
                                <div class="condition">OG</div>
                            </div>

                            <div class="grid-x filter-list-row" :key="index">
                                <div class="cell medium-4">
                                    <select class="property-selection" v-model="filter.property" @change="onPropertyChange(filter)">
                                        <option value="null" disabled>Vælg attribut</option>
                                        <option v-for="(property, index) in properties" :value="property" :key="index">{{ property.name }}</option>
                                    </select>
                                </div>

                                <div class="cell medium-2" v-if="filter.property ? filter.property.type !== 'dateTime' : true">
                                    <select class="operator-selection" :disabled="filter.property ? false : true" v-model="filter.operator" @change="filterObjects">
                                        <option v-if="filter.property ? filter.property.type === 'string' : false" v-for="(operator, index) in operators.string" :value="operator" :key="index">{{ operator | operatorAsPlainText }}</option>
                                        <option v-if="filter.property ? filter.property.type === 'int' || filter.property.type === 'float' : false" v-for="(operator, index) in operators.number" :value="operator" :key="index">{{ operator | operatorAsPlainText }}</option>
                                        <option v-if="filter.property ? filter.property.type === 'boolean' : false" v-for="(operator, index) in operators.boolean" :value="operator" :key="index">{{ operator | operatorAsPlainText }}</option>
                                    </select>
                                </div>

                                <div class="cell medium-5" v-if="filter.property ? filter.property.type === 'string' : true">
                                    <template v-for="(value, index) in filter.value">
                                        <div class="flexbox-center" v-if="index > 0">
                                            <div class="condition">ELLER</div>
                                        </div>
                                        <div class="flexbox-center">
                                            <template v-if="filter.property && filter.property.values">
                                                <select class="value-selection" :disabled="filter.property === null" v-model="filter.value[index]" @change="filterObjects">
                                                    <option value="null" disabled>Vælg en værdi</option>
                                                    <option v-for="(value, index) in filter.property.values" :value="value" :key="index">{{ value }}</option>
                                                </select>
                                            </template>
                                            <template v-else>
                                                <input type="text" class="value-input" :disabled="filter.property === null" v-model="filter.value[index]" @change="filterObjects" />
                                            </template>
                                            <button class="delete-value" v-show="filter.value.length > 1" @click="removeValue(filter, index)">
                                                <span class="visually-hidden">Fjern værdi</span>
                                                <icon name="trash" />
                                            </button>
                                            <button class="add-value" v-show="index === filter.value.length - 1 && filter.property !== null" @click="addValue(filter)">
                                                <span class="visually-hidden">Tilføj ekstra værdi</span>
                                                <icon name="plus" />
                                            </button>
                                        </div>
                                    </template>
                                </div>
                                <div class="cell medium-5" v-else-if="filter.property ? filter.property.type === 'int' || filter.property.type === 'float' : false">
                                    <template v-for="(value, index) in filter.value">
                                        <div class="flexbox-center" v-if="index > 0">
                                            <div class="condition">ELLER</div>
                                        </div>
                                        <div class="flexbox-center">
                                            <input type="number" class="value-input" :disabled="filter.property === null" v-model.number="filter.value[index]" @change="filterObjects" />
                                            <button class="delete-value" v-show="filter.value.length > 1" @click="removeValue(filter, index)">
                                                <span class="visually-hidden">Fjern værdi</span>
                                                <icon name="trash" />
                                            </button>
                                            <button class="add-value" v-show="index === filter.value.length - 1 && filter.property !== null" @click="addValue(filter)">
                                                <span class="visually-hidden">Tilføj ekstra værdi</span>
                                                <icon name="plus" />
                                            </button>
                                        </div>
                                    </template>
                                </div>
                                <div class="cell medium-5" v-if="filter.property ? filter.property.type === 'boolean' : false">
                                    <select class="value-selection" :disabled="filter.property === null" v-model="filter.value[index]" @change="filterObjects">
                                        <option value="null" disabled>Vælg en værdi</option>
                                        <option value="true">Ja</option>
                                        <option value="false">Nej</option>
                                    </select>
                                </div>
                                <div class="cell medium-7" v-else-if="filter.property ? filter.property.type === 'dateTime' : false">
                                    <div style="align-items: center; display: flex; gap: 12px;">
                                        <div class="calendar-input">
                                            <base-date-picker id="task-activity-startDate" :disabled="disabledDateFromDates[index]" :max-date="filter.value[1]" v-model="filter.value[0]">Startdato</base-date-picker>
                                            <icon class="calendar small-svg" name="calendar" />
                                        </div>
                                        <strong>-</strong>
                                        <div class="calendar-input">
                                            <base-date-picker id="task-activity-latestDate" :disabled="disabledDateToDates[index]" :min-date="filter.value[0]" v-model="filter.value[1]">Slutdato</base-date-picker>
                                            <icon class="calendar small-svg" name="calendar" />
                                        </div>
                                    </div>
                                </div>

                                <button class="delete-filter" @click="removeFilter(filter)">
                                    <span class="visually-hidden">Slet filter</span>
                                    <icon class="layers smaller-svg" name="trash" />
                                </button>
                            </div>
                        </template>
                    </div>

                    <div class="filter-buttons">
                        <button type="button" class="button-blue" v-if="selectedLayer !== '' && !loading" @click="addFilter()">+ Tilføj filter</button>
                        <button type="button" class="button-red" v-if="!hasFilters" @click="clearFilter()">Ryd filtre</button>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <div class="text-center modal-buttons">
                    <button type="button" class="button-green" :disabled="hasObjects" @click="addObjectsToActivity()">Tilføj til aktivitet</button>
                    <button type="button" class="button-naked" @click="closeModal()">Fortryd</button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import axios from './../../../store/api'
import BaseDatePicker from '@/components/base/BaseDatePicker.vue'

export default {
    name: 'selection-filter-modal',
    components: {
        BaseDatePicker
    },
    props: [
        'data'
    ],
    data () {
        return {
            show: true,
            selectedLayer: '',
            properties: null,
            filters: [],
            operators: {},
            objects: [],
            filteredObjects: null,
            loading: false,
            limitWarning: false,
            warningAmount: 100000,
            addObjectsLoading: false,

            disabledDateToDates: {},
            disabledDateFromDates: {}
        }
    },
    computed: {
        ...mapGetters({
            applicationSettings: 'getApplicationSettings',
            selectedSupplyType: 'selectedSupplyType'
        }),
        getLayers () {
            const mapServices = this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].mapServiceList.filter((service) => {
                return service.multiSelect === true
            })

            if (mapServices.length > 0) {
                return mapServices.map(service => service.layers.map(layer => ({ ...layer, supportedComparisonOperatorList: service.supportedComparisonOperatorList }))).flat()
            }

            return null
        },
        hasObjects () {
            return this.objects.length === 0 && !this.filteredObjects
        },
        hasFilters () {
            return this.filters.length === 0 && !this.filteredObjects
        }
    },
    watch: {
        selectedLayer () {
            this.filters = []
            this.addObjectsByWkt(this.data.wkt)
            this.getLayerProperties()
            this.getOperators()
        },
        filters: {
            handler () {
                this.filters.forEach((filter, index) => {
                    if (filter.property) {
                        this.disabledDateToDates[index] = {
                            to: this.filters[index].value[0] // Disable all dates after specific date
                        }
                        this.disabledDateFromDates[index] = {
                            from: this.filters[index].value[1] // Disable all dates before specific date
                        }
                    }
                })
            },
            deep: true
        }
    },
    methods: {
        ...mapActions({
            addActivityObjectsByFilter: 'addActivityObjectsByFilter'
        }),
        addFilter () {
            if (this.selectedLayer === '') {
                alert('Vælg et lag for at kunne tilføje et filter.')
            } else {
                this.filters.push({
                    property: null,
                    operator: 'IsEqualTo',
                    value: [null]
                })
            }
        },
        removeFilter (filter) {
            this.filters = this.filters.filter(f => f.property !== filter.property)
            if (this.filters.length > 0) {
                this.filterObjects()
            } else {
                this.filteredObjects = null
            }
        },
        clearFilter () {
            this.filters = []
            this.filteredObjects = null
        },
        addValue (filter) {
            this.filters.find(f => f === filter).value.push([null])
        },
        removeValue (filter, index) {
            this.filters.find(f => f === filter).value.splice(index, 1)
            this.filterObjects()
        },
        async addObjectsToActivity () {
            if (this.filteredObjects || this.objects.length > 0) {
                if (this.filteredObjects === null) {
                    this.addObjectsLoading = true
                    const payload = {
                        'objectFilter': {
                            'datasourceId': this.selectedLayer.dataSourceId,
                            'businessAreaTypeId': this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].businessAreaTypeId,
                            'polygonWkt': this.data.wkt,
                            'andPropertyFilterList': []
                        },
                        'taskId': this.data.taskId,
                        'activityIdList': this.data.selectedActivity
                    }
                    await this.addActivityObjectsByFilter(payload)
                    this.data.getBoundedObjects(true)
                    this.closeModal()
                } else {
                    this.addObjectsLoading = true

                    const payload = {
                        'objectFilter': {
                            'datasourceId': this.selectedLayer.dataSourceId,
                            'businessAreaTypeId': this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].businessAreaTypeId,
                            'polygonWkt': this.data.wkt,
                            'andPropertyFilterList': this.filters.flatMap(f => {
                                if (f.property.type === 'dateTime') {
                                    if (f.value[0] && f.value[1]) {
                                        // From (greaterThan) and to date (LessThan)
                                        return [{
                                            'propertyName': f.property.name,
                                            'comparisonOperator': 'IsGreaterThanOrEqualTo',
                                            'literalList': [f.value[0]]
                                        },
                                        {
                                            'propertyName': f.property.name,
                                            'comparisonOperator': 'IsLessThanOrEqualTo',
                                            'literalList': [f.value[1]]
                                        }]
                                    } else if (f.value[0]) {
                                        // From date (greaterThan)
                                        return {
                                            'propertyName': f.property.name,
                                            'comparisonOperator': 'IsGreaterThanOrEqualTo',
                                            'literalList': [f.value[0]]
                                        }
                                    } else if (f.value[1]) {
                                        // To date (LessThan)
                                        return {
                                            'propertyName': f.property.name,
                                            'comparisonOperator': 'IsLessThanOrEqualTo',
                                            'literalList': [f.value[1]]
                                        }
                                    }
                                }
                                return {
                                    'propertyName': f.property.name,
                                    'comparisonOperator': f.operator,
                                    'literalList': f.value
                                }
                            })
                        },
                        'taskId': this.data.taskId,
                        'activityIdList': this.data.selectedActivity
                    }

                    await this.addActivityObjectsByFilter(payload)
                    this.data.getBoundedObjects(true)
                    this.closeModal()
                }
            }
        },
        addObjectsByWkt (wkt) {
            this.limitWarning = false
            this.objects = []
            const dataSourceId = this.selectedLayer.dataSourceId
            if (dataSourceId) {
                this.loading = true
                axios.post('Gis/GetGisObjects', {
                    datasourceId: dataSourceId,
                    businessAreaTypeId: this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].businessAreaTypeId,
                    polygonWkt: wkt,
                    andPropertyFilterList: []
                })
                    .then(response => {
                        this.loading = false
                        if (response.data.length >= this.warningAmount) {
                            console.warn('Der er 100.000, vælg venligst et mindre område.')
                            this.limitWarning = true
                        }
                        response.data.forEach(obj => {
                            const mapService = this.applicationSettings.kortinfoSettings[this.selectedSupplyType.code].mapServiceList.find((service) => {
                                return service.type === 'ogc' || service.type === 'arcgis' || service.type === 'kortinfo' || service.type === 'ogckortinfo'
                            })

                            if (mapService && mapService.layers) {
                                const layer = mapService.layers.find(layer => layer.dataSourceId === dataSourceId)

                                if (layer) {
                                    const geometry = obj.propertyList.find(p => p.name.toUpperCase() === layer.geometryPropertyName || p.name.toUpperCase() === 'CG_GEOMETRY')

                                    this.objects.push({
                                        'objectKey': obj.driveId,
                                        'fieldGeometry': geometry ? geometry.value : null,
                                        'dataSourceId': dataSourceId,
                                        'layerName': this.selectedLayer.name,
                                        'objectDescription': obj.description,
                                        'objectProperty': obj.propertyList
                                    })
                                }
                            }
                        })
                    })
            }
        },
        closeModal () {
            this.$emit('close')
        },
        filterObjects () {
            this.$nextTick(() => {
                this.filteredObjects = this.objects.filter((object) => {
                    return this.filters.every((filter) => {
                        const objectProperty = object.objectProperty.find(property => property.name === filter.property.name)

                        if (objectProperty) {
                            if (filter.property.type === 'dateTime') {
                                let convertedValueDate = new Date(objectProperty.value)

                                if (objectProperty.value === convertedValueDate.toISOString()) {
                                    const objectPropertyDate = new Date(objectProperty.value)
                                    const timeOffset = new Date(objectPropertyDate.getTime() - objectPropertyDate.getTimezoneOffset() * -60000)

                                    convertedValueDate = new Date(timeOffset.getFullYear(), timeOffset.getMonth(), timeOffset.getDate())
                                }

                                if (filter.value) {
                                    if (filter.value[0] && filter.value[1]) {
                                        // From and to date
                                        const userFromValueConverted = new Date(filter.value[0].getFullYear(), filter.value[0].getMonth(), filter.value[0].getDate())
                                        const userToValueConverted = new Date(filter.value[1].getFullYear(), filter.value[1].getMonth(), filter.value[1].getDate())

                                        return ((userFromValueConverted <= convertedValueDate) && (userToValueConverted >= convertedValueDate))
                                    } else if (filter.value[0]) {
                                        // From date
                                        const userValueConverted = new Date(filter.value[0].getFullYear(), filter.value[0].getMonth(), filter.value[0].getDate())

                                        return userValueConverted <= convertedValueDate
                                    } else if (filter.value[1]) {
                                        // To date
                                        const userValueConverted = new Date(filter.value[1].getFullYear(), filter.value[1].getMonth(), filter.value[1].getDate())

                                        return userValueConverted >= convertedValueDate
                                    }
                                }
                            } else if (filter.property.type === 'int' || filter.property.type === 'float') {
                                const objectValue = parseFloat(objectProperty.value)
                                return filter.value.some((value) => {
                                    if (filter.operator === 'IsLessThan') {
                                        return objectValue < parseFloat(value)
                                    } else if (filter.operator === 'IsLessThanOrEqualTo') {
                                        return objectValue <= parseFloat(value)
                                    } else if (filter.operator === 'IsEqualTo') {
                                        return objectValue === parseFloat(value)
                                    } else if (filter.operator === 'IsNotEqualTo') {
                                        return objectValue !== parseFloat(value)
                                    } else if (filter.operator === 'IsGreaterThan') {
                                        return objectValue > parseFloat(value)
                                    } else if (filter.operator === 'IsGreaterThanOrEqualTo') {
                                        return objectValue >= parseFloat(value)
                                    }
                                })
                            } else if (filter.property.type === 'boolean' || filter.property.type === 'string') {
                                const objectValue = objectProperty.value

                                return filter.value.some((value) => {
                                    if (filter.operator === 'IsEqualTo') {
                                        return objectValue === value
                                    } else if (filter.operator === 'IsNotEqualTo') {
                                        return objectValue !== value
                                    }
                                })
                            }
                        } else if (!objectProperty && filter.operator === 'IsNotEqualTo') {
                            return true
                        }
                    })
                })
            })
        },
        onPropertyChange (filter) {
            if (filter.property.type === 'dateTime') {
                filter.value = ['', '']
            } else {
                filter.value = [null]
            }
        },
        getLayerProperties () {
            this.properties = this.selectedLayer.editObjectProperties
        },
        getOperators () {
            let operators = {
                boolean: [],
                number: [],
                string: []
            }

            this.selectedLayer.supportedComparisonOperatorList.forEach(op => {
                if (op === 'IsLessThan') {
                    operators.number.push('IsLessThan')
                } else if (op === 'IsLessThanOrEqualTo') {
                    operators.number.push('IsLessThanOrEqualTo')
                } else if (op === 'IsEqualTo') {
                    operators.boolean.push('IsEqualTo')
                    operators.number.push('IsEqualTo')
                    operators.string.push('IsEqualTo')
                } else if (op === 'IsNotEqualTo') {
                    operators.boolean.push('IsNotEqualTo')
                    operators.number.push('IsNotEqualTo')
                    operators.string.push('IsNotEqualTo')
                } else if (op === 'IsGreaterThan') {
                    operators.number.push('IsGreaterThan')
                } else if (op === 'IsGreaterThanOrEqualTo') {
                    operators.number.push('IsGreaterThanOrEqualTo')
                }
            })
            this.operators = operators
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/components/shared/map/selection-filter-modal.vue